import React from 'react';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';
import { Divider, Typography } from '@mui/material';
import { IImage } from '@lialo/core/lib/models';

import useContentWidth from '../../hooks/useContentWidth';
import { ImageSlider, ImageSlide, SliderImage } from '../../Slider';
import { useTranslation } from 'react-i18next';
import { AudioPlayer } from '../../AudioPlayer';
import { chooseTourImageLink } from '@lialo/core/lib/utils/helper/image-link';

const PREFIX = 'BaseSection';

const classes = {
  withImages: `${PREFIX}-withImages`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
};

const StyledSection = styled('section')(({ theme }) => ({
  [`& .${classes.withImages}`]: {
    paddingBottom: theme.spacing(1.5),
  },

  [`& .${classes.title}`]: {
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },

  [`& .${classes.content}`]: {
    padding: theme.spacing(1.5, 3, 2, 3),
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(4, 2, 1, 2),
}));

interface IProps {
  title: string;
  id: string;
  images?: IImage[];
  children: React.ReactNode;
  hideDivider?: boolean;
  audioSrc?: string;
}

const BaseSection = (props: IProps) => {
  const width = useContentWidth(true);
  const { t } = useTranslation();

  return (
    <>
      <StyledSection id={props.id}>
        <Typography
          className={classNames(classes.title, {
            [classes.withImages]: !!props.images,
          })}
          variant={'h6'}
          component={'h3'}
        >
          {props.title}
        </Typography>
        {props.images && (
          <ImageSlider>
            {props.images.map((img, idx) => (
              <ImageSlide key={img.url} caption={img.caption} backgroundImg={chooseTourImageLink(img.url, width)}>
                <SliderImage
                  originalSrc={img.url}
                  alt={img.caption ?? t('tour_content.fallback_img_alt', { title: props.title })}
                  width={width}
                  aspectRatio={'16 / 9'}
                  slideNumber={idx}
                />
              </ImageSlide>
            ))}
          </ImageSlider>
        )}
        {props.audioSrc && <AudioPlayer audioSrc={props.audioSrc} />}
        <div className={classes.content}>{props.children}</div>
      </StyledSection>
      {!props.hideDivider && <StyledDivider variant={'middle'} />}
    </>
  );
};

export default BaseSection;
